import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { TraderListCollapsePrefs } from 'utils/traderListCollapse';

/**
 * Per-account collapse state for trader want lists, shared by Send Cards and
 * Activity. Both writes patch the cached GET at once and reconcile from the
 * response (each returns the full GET shape). They deliberately invalidate
 * nothing: a toggle is one PUT and no refetch of anything, packages and the
 * activity feed included.
 */
export const traderListCollapseApi = createApi({
  reducerPath: 'traderListCollapseApi',
  baseQuery: baseQuery,
  tagTypes: ['TraderListCollapse'],
  endpoints: (builder) => ({
    getTraderListCollapse: builder.query<TraderListCollapsePrefs, void>({
      query: () => ({
        url: '/me/trader-list-collapse',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['TraderListCollapse'],
    }),

    /** Sets the flag and clears every per-trader override, so the page ends up uniform. */
    setCollapseAll: builder.mutation<TraderListCollapsePrefs, { collapseAll: boolean }>({
      query: (body) => ({
        url: '/me/trader-list-collapse/all',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted({ collapseAll }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          traderListCollapseApi.util.updateQueryData('getTraderListCollapse', undefined, (draft) => {
            // No cached GET (it failed or has not answered): RTK passes undefined, not a draft.
            if (!draft) return { collapseAll, overrides: {} };
            draft.collapseAll = collapseAll;
            draft.overrides = {};
            return undefined;
          }),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            traderListCollapseApi.util.updateQueryData('getTraderListCollapse', undefined, () => data),
          );
        } catch {
          patch.undo();
        }
      },
    }),

    setTraderCollapsed: builder.mutation<
      TraderListCollapsePrefs,
      { traderUserId: number | string; collapsed: boolean }
    >({
      query: ({ traderUserId, collapsed }) => ({
        url: `/me/trader-list-collapse/${traderUserId}`,
        method: 'PUT',
        body: { collapsed },
        requiresAuth: true,
      }),
      async onQueryStarted({ traderUserId, collapsed }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          traderListCollapseApi.util.updateQueryData('getTraderListCollapse', undefined, (draft) => {
            if (!draft) return { collapseAll: false, overrides: { [String(traderUserId)]: collapsed } };
            draft.overrides = { ...(draft.overrides ?? {}), [String(traderUserId)]: collapsed };
            return undefined;
          }),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            traderListCollapseApi.util.updateQueryData('getTraderListCollapse', undefined, () => data),
          );
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetTraderListCollapseQuery,
  useSetCollapseAllMutation,
  useSetTraderCollapsedMutation,
} = traderListCollapseApi;
